<template>
  <AppStack direction="flex-col" class="px-5 gap-3">
    <AppStack direction="flex-col" class="gap-2">
      <AppButton
        v-for="(action, index) in actions"
        :key="index"
        :text="action.text"
        :variant="action.variant || 'outline-secondary'"
        @click="action?.onClick()"
      />
    </AppStack>
    <AppButton :text="t('common.close')" variant="tertiary" @click="emit('dismiss')" />

    <ion-action-sheet
      :is-open="reassignSheetOpen"
      :header="t('order.actions.reassignPickHeader')"
      :buttons="reassignSheetButtons"
      @did-dismiss="reassignSheetOpen = false"
    />
  </AppStack>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { IonActionSheet } from '@ionic/vue'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import { type ComponentProps as ExtractComponentProps } from '@restify/packages/types/index'
import useStores, { type Stores } from '~/composables/useStores'
import AppButton from '~/components/AppButton.vue'
import { useAuthStore } from '~/stores/auth'
import { useShiftsLogic } from '@restify/packages/composables/useShiftsLogic'
import { shiftWindowQuery } from '@restify/packages/helpers/shift-window'

const emit = defineEmits<{
  (e: 'dismiss'): void
  (e: 'order-cancelled', orderId: string): void
}>()

const props = defineProps({
  order: {
    type: Object as PropType<Stores['orders']['Result'] | null>,
    default: null,
  },
})

const {
  orders: OrdersStore,
  users: UsersStore,
  shifts: ShiftsStore,
} = useStores()
const { t } = useI18n()
const AuthStore = useAuthStore()
const { shift, shiftIsHappening } = useShiftsLogic(AuthStore)

const currentWaiterId = computed(() => {
  return props.order?.staff.find((staff) => staff.role === 'waiter')?.staffId
})

// Elevated roles (manager / app-owner / superadmin) can reassign a
// table to any assignable staff member — floor waiters can only
// take a table for themselves via "Assign to me" above. `role` here
// is `profile.role`; permissions.type isn't checked because managers
// on the floor also have permissions.type === 'user' in some rigs.
const canReassignToAnyone = computed(() => {
  const role = AuthStore.reactiveUser?.profile.role
  const permType = AuthStore.reactiveUser?.permissions?.type

  return (
    role === 'manager' ||
    permType === 'manager' ||
    permType === 'app-owner' ||
    permType === 'superadmin'
  )
})

// Shifts within the shared "active" window (now ±30 min, matches
// InHousePage / DashboardPage). Only assignable staff who have such
// a shift can take a table — and we write THAT shift's id onto the
// order.staff slot below so downstream (payroll, dashboard) attaches
// the table to their real shift, not the current user's.
const activeShifts = computed(() => {
  return ShiftsStore.findInStore({
    ...shiftWindowQuery(Date.now()),
  })
    .map((r) => r.value)
    .filter((s): s is NonNullable<typeof s> => !!s)
})

// staffId → their current active shift. First shift wins if a staff
// happens to be on multiple overlapping ones (rare, but the wrapper
// pattern for cross-role shifts). Staff who already ended their
// shift (`staffLogs[staffId].endedAt`) are excluded.
const shiftByStaffId = computed(() => {
  const map: Record<string, NonNullable<Stores['shifts']['Result']>> = {}
  for (const shiftDoc of activeShifts.value) {
    for (const staffId of shiftDoc.assignedStaffIds || []) {
      if (shiftDoc.staffLogs?.[staffId]?.endedAt) continue
      if (!map[staffId]) map[staffId] = shiftDoc
    }
  }

  return map
})

// Assignable = staff currently on shift, minus the current user
// (they have their own "Assign to me" above), scoped to the current
// storefront. Read against the local store — refresh fires on the
// button tap so the sheet is fresh next time.
const assignableStaff = computed(() => {
  const currentStorefrontId =
    AuthStore.parsedAccessToken?.storefrontId ||
    AuthStore.reactiveUser?.permissions?.storefrontIds?.[0]
  const currentUserId = AuthStore.reactiveUser?._id
  if (!currentStorefrontId) return []

  const byId = shiftByStaffId.value

  return UsersStore.findInStore({
    status: 'public',
    'profile.role': { $in: ['waiter', 'manager'] },
    $sort: { 'profile.firstName': 1 },
  })
    .map((r) => r.value)
    .filter(
      (u): u is NonNullable<typeof u> =>
        !!u &&
        (u.permissions?.storefrontIds || []).includes(currentStorefrontId) &&
        u._id !== currentUserId &&
        !!byId[String(u._id)],
    )
})

const reassignSheetOpen = ref(false)

const patchOrderStaff = (staffId: string) => {
  if (!props.order || !staffId) return
  const pickedShiftId = shiftByStaffId.value[staffId]?._id
  const newStaff = {
    shiftId: pickedShiftId,
    staffId,
    role: 'waiter' as const,
    processed: false,
  }
  OrdersStore.patch(
    props.order._id,
    currentWaiterId.value
      ? { $set: { 'staff.$': newStaff } }
      : { $push: { staff: newStaff } },
    currentWaiterId.value
      ? { query: { 'staff.staffId': currentWaiterId.value } }
      : {},
  )
  emit('dismiss')
}

const reassignSheetButtons = computed(() => {
  const staffButtons = assignableStaff.value.map((u) => ({
    text:
      `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.trim() ||
      u.email ||
      '—',
    handler: () => patchOrderStaff(String(u._id)),
  }))

  return [
    ...(staffButtons.length
      ? staffButtons
      : [
          {
            text: t('order.actions.reassignNoStaffOnShift'),
            handler: () => {
              // No-op — informational entry only.
            },
          },
        ]),
    {
      text: t('common.cancel'),
      role: 'cancel' as const,
    },
  ]
})

const actions = computed<
  {
    text: string
    variant?: ExtractComponentProps<typeof AppButton>['variant']
    onClick: () => void
  }[]
>(() => {
  return [
    {
      text: t('order.actions.addOrderToTable'),
      onClick: () => {
        if (!props.order?.storefrontPlaceIds?.[0]) {
          console.warn('No storefront place to create order')

          return
        }

        if (!AuthStore.reactiveUser) {
          console.warn('No active user')

          return
        }

        OrdersStore.create({
          status: {
            name: 'created',
            createdAt: new Date().getTime(),
          },
          menuItems: [],
          batches: [],
          tabName: '',
          fees: [],
          type: 'inHouseService',
          storefrontPlaceIds: [props.order?.storefrontPlaceIds[0]],
          staff: [
            {
              shiftId:
                shift.value && shiftIsHappening.value
                  ? shift.value._id
                  : undefined,
              staffId: AuthStore.reactiveUser?._id,
              role: AuthStore.reactiveUser.profile.role as 'waiter',
              processed: false,
            },
          ],
        }).then(() => emit('dismiss'))
      },
    },
    // "Assign to me" — waiter or manager (managers also work the floor
    // and need to take over tables). Hidden when the current user is
    // already the assigned waiter. The waiter slot is REPLACED (not
    // appended) via the positional `staff.$` operator matched by the
    // existing waiter's staffId — floor tables carry exactly one
    // waiter at a time; we don't want an ever-growing staff array.
    // When no waiter is on the order yet, $push instead.
    //
    // Note: when a manager takes over, we still write role: 'waiter'
    // into the slot — semantically they're standing in AS the waiter
    // for this table, and downstream reports (payroll, shift dashboard)
    // key off the 'waiter' role. Their own profile.role stays intact.
    ...((AuthStore.reactiveUser?.profile.role === 'waiter' ||
      AuthStore.reactiveUser?.profile.role === 'manager') &&
    currentWaiterId.value !== AuthStore.reactiveUser?._id
      ? [
          {
            text: t('order.actions.assignToMe'),
            onClick: () => {
              if (!props.order || !AuthStore.reactiveUser) return

              const newStaff = {
                shiftId:
                  shift.value && shiftIsHappening.value
                    ? shift.value._id
                    : undefined,
                staffId: AuthStore.reactiveUser._id,
                role: 'waiter' as const,
                processed: false,
              }

              OrdersStore.patch(
                props.order._id,
                currentWaiterId.value
                  ? {
                      $set: {
                        'staff.$': newStaff,
                      },
                    }
                  : {
                      $push: {
                        staff: newStaff,
                      },
                    },
                currentWaiterId.value
                  ? {
                      query: {
                        'staff.staffId': currentWaiterId.value,
                      },
                    }
                  : {},
              )

              emit('dismiss')
            },
          },
        ]
      : []),
    // "Reassign to…" — visible for elevated roles when there's at
    // least one other assignable staff member. Opens an action sheet
    // with the list; picking a person patches the order the same way
    // "Assign to me" does. Waiters see only "Assign to me" above.
    ...(canReassignToAnyone.value
      ? [
          {
            text: t('order.actions.reassignTo'),
            onClick: () => {
              // Fire-and-forget refresh so the list is fresh next
              // time the sheet opens; the current tap renders
              // whatever's already in the store to avoid a spinner.
              // Both stores get a nudge because the list depends on
              // Users × Shifts (staff currently on shift).
              UsersStore.find({
                query: {
                  status: 'public',
                  'profile.role': { $in: ['waiter', 'manager'] },
                  $limit: 100,
                },
              }).catch(() => {})
              ShiftsStore.find({
                query: {
                  ...shiftWindowQuery(Date.now()),
                  $limit: 100,
                },
              }).catch(() => {})
              reassignSheetOpen.value = true
            },
          },
        ]
      : []),
    ...(AuthStore.reactiveUser?.profile.role === 'manager' &&
    props.order?.status.name === 'cancelled'
      ? [
          {
            text: t('order.actions.restoreOrder'),
            variant: 'outline-secondary' as const,
            onClick: () => {
              const orderId = props.order?._id

              if (!orderId) {
                return console.warn('No order to cancel')
              }

              OrdersStore.patch(orderId, {
                'status.name': 'created',
              }).then(() => emit('dismiss'))
            },
          },
        ]
      : []),
    ...(AuthStore.reactiveUser?.profile.role === 'manager' ||
    !Object.keys(props.order?.menuItems || {}).length
      ? [
          {
            text: t('order.actions.cancelOrder'),
            variant: 'outline-destructive' as const,
            onClick: () => {
              const orderId = props.order?._id

              if (!orderId) {
                return console.warn('No order to cancel')
              }

              OrdersStore.patch(orderId, {
                'status.name': 'cancelled',
                'status.cancelledAt': new Date().getTime(),
              }).then(() => emit('order-cancelled', orderId))
            },
          },
        ]
      : []),
  ]
})
</script>
